import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { LocaleService } from '@next/core-lib/i18n';
import { Observable } from 'rxjs';

import {
  FmsDiagnoseSprayApplication,
  FmsDiagnoseSprayCropField,
  FmsDiagnoseSpraysResponse,
} from '../../../shared/models/fms-diagnose.model';

export type FmsDiagnoseSpraysState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'syncing' }
  | { kind: 'loaded'; data: FmsDiagnoseSpraysResponse }
  | { kind: 'error' };

export type FmsDiagnoseSprayCategory = 'virus' | 'phytophthora' | 'downyMildew' | 'notLinked';

export interface FmsDiagnoseSpraysSummary {
  cropFieldCount: number;
  sprayCount: number;
  virusCount: number;
  phytophthoraCount: number;
  downyMildewCount: number;
  notLinkedCount: number;
}

@Component({
  selector: 'app-fms-diagnose-sprays-tab',
  templateUrl: './fms-diagnose-sprays-tab.component.html',
  styleUrls: ['./fms-diagnose-sprays-tab.component.scss'],
  standalone: false,
})
export class FmsDiagnoseSpraysTabComponent {
  @Input({ required: true }) spraysState$!: Observable<FmsDiagnoseSpraysState>;

  @Output() readonly retryRequested = new EventEmitter<void>();

  private readonly locale = inject(LocaleService);

  visibleCropFields(response: FmsDiagnoseSpraysResponse): FmsDiagnoseSprayCropField[] {
    return response.cropFields
      .filter((cropField) => cropField.sprayApplications.length > 0)
      .map((cropField) => ({
        ...cropField,
        sprayApplications: [...cropField.sprayApplications].sort(
          (left, right) =>
            new Date(right.applicationDate).getTime() - new Date(left.applicationDate).getTime(),
        ),
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  spraySummary(response: FmsDiagnoseSpraysResponse): FmsDiagnoseSpraysSummary {
    const cropFields = this.visibleCropFields(response);
    const sprayApplications = cropFields.flatMap((cropField) => cropField.sprayApplications);

    return {
      cropFieldCount: cropFields.length,
      sprayCount: sprayApplications.length,
      virusCount: sprayApplications.filter((spray) => spray.isVirusMonitor).length,
      phytophthoraCount: sprayApplications.filter((spray) => spray.isPhytophthora).length,
      downyMildewCount: sprayApplications.filter((spray) => spray.isDownyMildew).length,
      notLinkedCount: sprayApplications.filter((spray) => this.isNotLinked(spray)).length,
    };
  }

  categories(spray: FmsDiagnoseSprayApplication): FmsDiagnoseSprayCategory[] {
    const categories: FmsDiagnoseSprayCategory[] = [];
    if (spray.isVirusMonitor) {
      categories.push('virus');
    }
    if (spray.isPhytophthora) {
      categories.push('phytophthora');
    }
    if (spray.isDownyMildew) {
      categories.push('downyMildew');
    }
    return categories.length > 0 ? categories : ['notLinked'];
  }

  cropName(cropField: FmsDiagnoseSprayCropField): string {
    return cropField.cropName.trim() || this.unavailable();
  }

  unavailable(): string {
    return this.locale.translate('fmsDiagnose.shared.unavailable');
  }

  requestRetry(): void {
    this.retryRequested.emit();
  }

  private isNotLinked(spray: FmsDiagnoseSprayApplication): boolean {
    return !spray.isVirusMonitor && !spray.isPhytophthora && !spray.isDownyMildew;
  }
}
